import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Building2,
  FileText,
  Loader2,
  MapPin,
  Package,
  QrCode,
  User as UserIcon,
  X,
} from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { DocumentsList } from './DocumentsList';
import { HistoriqueTimeline } from './HistoriqueTimeline';
import { ModalPortal } from '../ModalPortal';
import { PdfExportButton } from '../shared/PdfExportButton';
import { QrLabelPrint } from '../shared/QrLabelPrint';
import { EquipementsListPanel } from '../equipements/EquipementsListPanel';
import { EquipementDetailDrawer } from '../equipements/EquipementDetailDrawer';

/**
 * Drawer détail bâtiment (US-BAT-02 + 06 + 07).
 *
 * Affiche :
 *  - Identité (code, nom, type, statut, adresse, surface, responsable)
 *  - Documents techniques attachés (US-BAT-06)
 *  - Timeline historique (US-BAT-07)
 *  - Custom fields (Form Builder)
 */
interface BatimentDetail {
  id: string;
  code: string;
  nom: string;
  type: string;
  statut: string;
  adresse: string | null;
  surface_totale: number | null;
  nombre_etages: number;
  responsable: { id: string; first_name: string; last_name: string; email: string } | null;
  custom_fields: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export function BatimentDetailDrawer({
  batimentId,
  onClose,
}: {
  batimentId: string;
  onClose(): void;
}) {
  const [detail, setDetail] = useState<BatimentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<'info' | 'equipements' | 'documents' | 'qr' | 'historique'>(
    'info',
  );
  const [openEquipementId, setOpenEquipementId] = useState<string | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ data: BatimentDetail }>(`/batiments/${batimentId}`);
        setDetail(res.data.data);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [batimentId]);

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} aria-hidden />
      <aside className="fixed top-0 right-0 z-50 h-screen w-full max-w-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-primary-600" />
            Détail bâtiment
          </h2>
          <div className="flex items-center space-x-1">
            {detail && (
              <PdfExportButton
                targetRef={pdfRef}
                filename={`fiche-batiment-${detail.code}.pdf`}
                label="PDF"
                onBeforeExport={() => setTab('info')}
              />
            )}
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="border-b border-gray-200 flex-shrink-0">
          <nav className="flex">
            <TabButton active={tab === 'info'} onClick={() => setTab('info')}>
              Informations
            </TabButton>
            <TabButton active={tab === 'equipements'} onClick={() => setTab('equipements')}>
              <Package className="w-3.5 h-3.5 inline mr-1" />
              Équipements
            </TabButton>
            <TabButton active={tab === 'documents'} onClick={() => setTab('documents')}>
              <FileText className="w-3.5 h-3.5 inline mr-1" />
              Documents
            </TabButton>
            <TabButton active={tab === 'qr'} onClick={() => setTab('qr')}>
              <QrCode className="w-3.5 h-3.5 inline mr-1" />
              QR
            </TabButton>
            <TabButton active={tab === 'historique'} onClick={() => setTab('historique')}>
              Historique
            </TabButton>
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2 mb-4">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          {loading || !detail ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : tab === 'info' ? (
            <div ref={pdfRef} className="space-y-4">
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Identité
                </h3>
                <dl className="text-sm space-y-1.5">
                  <Row label="Code" value={detail.code} />
                  <Row label="Nom" value={detail.nom} />
                  <Row label="Type" value={detail.type} />
                  <Row label="Statut" value={detail.statut.replace(/_/g, ' ')} />
                  <Row label="Nombre d'étages" value={String(detail.nombre_etages)} />
                  <Row
                    label="Surface totale"
                    value={detail.surface_totale ? `${detail.surface_totale} m²` : '—'}
                  />
                </dl>
              </section>

              {detail.adresse && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center">
                    <MapPin className="w-3.5 h-3.5 mr-1" />
                    Adresse
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.adresse}</p>
                </section>
              )}

              {detail.responsable && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center">
                    <UserIcon className="w-3.5 h-3.5 mr-1" />
                    Responsable
                  </h3>
                  <p className="text-sm text-gray-700">
                    {detail.responsable.first_name} {detail.responsable.last_name}
                    <span className="text-gray-400 ml-2">{detail.responsable.email}</span>
                  </p>
                </section>
              )}

              {Object.keys(detail.custom_fields ?? {}).length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Champs personnalisés
                  </h3>
                  <dl className="text-sm space-y-1.5">
                    {Object.entries(detail.custom_fields).map(([k, v]) => (
                      <Row key={k} label={k} value={String(v ?? '—')} />
                    ))}
                  </dl>
                </section>
              )}

              <section className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  Créé le {new Date(detail.created_at).toLocaleString()}
                  <br />
                  Modifié le {new Date(detail.updated_at).toLocaleString()}
                </p>
              </section>
            </div>
          ) : tab === 'equipements' ? (
            <EquipementsListPanel batimentId={batimentId} onEquipementClick={setOpenEquipementId} />
          ) : tab === 'documents' ? (
            <DocumentsList entityType="batiment" entityId={batimentId} />
          ) : tab === 'qr' ? (
            <QrLabelPrint
              code={detail.code}
              label={detail.nom}
              url={`${window.location.origin}/batiments/${detail.id}`}
            />
          ) : (
            <HistoriqueTimeline entityType="batiment" entityId={batimentId} />
          )}
        </div>
      </aside>

      {openEquipementId && (
        <EquipementDetailDrawer
          equipementId={openEquipementId}
          onClose={() => setOpenEquipementId(null)}
        />
      )}
    </ModalPortal>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'text-primary-600 border-primary-500 bg-primary-50/50'
          : 'text-gray-600 border-transparent hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-gray-500 text-xs">{label}</dt>
      <dd className="text-gray-900 text-right break-all">{value}</dd>
    </div>
  );
}
